import { rm, combineReducers } from 'utils';
import {
  BaseOrderLessInitialState,
  BaseOrderlessAction,
  BaseOrderlessState,
  baseFormReducer,
  baseOrderLessReducer,
  BaseAction
} from 'components/CustomerSupportForms/Utils';
import { isValidEmail } from 'components/Shared/Form';
import { isValidPhoneNumber } from 'views/CheckoutPage/validation';
import { isCustomerContactValid } from '../Components/CustomerContact/utils';
import { CustomerAddress } from 'models/CustomerAddress';
import { SupportProductModel } from 'models/SupportProductModel';

type InternalState = BaseOrderlessState & {
  subject: string;
  question: string;
  address?: CustomerAddress;
  products: SupportProductModel[];
  productsError?: string;
};

export const priceMatchInitialState: InternalState = {
  ...BaseOrderLessInitialState,
  subject: '',
  question: '',
  address: undefined,
  products: [],
  productsError: ''
};

type PriceMatchAction =
  | { type: 'question'; value: string }
  | { type: 'subject'; value: string }
  | { type: 'address'; value: CustomerAddress | undefined }
  | { type: 'products'; value: SupportProductModel[] }
  | { type: 'productsError'; value: string }
  | { type: 'productChanged'; value: SupportProductModel };

type Action = PriceMatchAction | BaseOrderlessAction | BaseAction;

const getProducts = (
  product: SupportProductModel,
  selectedProducts: SupportProductModel[]
): SupportProductModel[] => {
  if (selectedProducts.some(sp => sp.productId === product.productId)) {
    return selectedProducts.map(sp => {
      if (sp.productId === product.productId) {
        return { ...sp, quantity: product.quantity };
      }
      return sp;
    });
  }

  selectedProducts.push({
    productId: product.productId,
    productName: product.productName,
    productRecievedDate: product.productRecievedDate,
    quantity: product.quantity,
    unit: product.unit
  });

  return selectedProducts;
};

export const priceMatchReducer = (
  state: InternalState,
  action: PriceMatchAction
): InternalState => {
  switch (action.type) {
    case 'subject':
      return { ...state, subject: action.value };
    case 'question':
      return { ...state, question: action.value };
    case 'address':
      return { ...state, address: action.value };
    case 'products':
      return { ...state, products: action.value };
    case 'productsError':
      return { ...state, productsError: action.value };
    case 'productChanged':
      return { ...state, products: getProducts(action.value, state.products) };
    default:
      return state;
  }
};

export const reducer = combineReducers<InternalState, Action>(
  baseOrderLessReducer,
  baseFormReducer,
  priceMatchReducer
);

export const isValid = (
  state: InternalState,
  addressChanging: boolean,
  dispatch: (action: Action) => void
) => {
  let isValid = true;
  const { name, subject, question, email, phone, address } = state;

  if (address) {
    if (addressChanging) {
      dispatch({
        type: 'addressError',
        value: rm('customerpage_returns_validation_error_address')
      });
      isValid = false;
    }
    if (state.products.filter(p => p.quantity > 0).length === 0) {
      dispatch({
        type: 'productsError',
        value: rm('customerpage_returns_validation_error_products')
      });
      isValid = false;
    }
    if (question === '') {
      dispatch({
        type: 'generalError',
        value: rm('CustomerService_Question_Product_Validation_Error')
      });
      isValid = false;
    }
  } else {
    if (question === '' || subject === '' || email === '' || name === '' || phone === '') {
      dispatch({
        type: 'generalError',
        value: rm('CustomerService_Question_Product_Validation_Error')
      });
      isValid = false;
    }
    if (!isValidEmail(email) || !isValidPhoneNumber(phone)) {
      dispatch({
        type: 'addressError',
        value: rm('CustomerService_Quote_Request_Address_Validation_Error')
      });
      isValid = false;
    }
  }

  isValid = isCustomerContactValid({ state, valid: isValid, dispatch });

  return isValid;
};

export const productChanged = (
  product: SupportProductModel,
  dispatch: (action: Action) => void
) => {
  dispatch({ type: 'productChanged', value: product });
  dispatch({ type: 'productsError', value: '' });
};
